import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
    type AuditChipId,
    type AuditFilterChip,
    type AuditLogFilters,
    DEFAULT_AUDIT_LOG_FILTERS,
    isDefaultAuditLogFilters,
    removeAuditFilterChip,
    useAuditText,
} from '@/domain/auditLog';

import FilterChip from './FilterChip';

import styles from './ActiveFilterChips.module.scss';

interface ActiveFilterChipsProps {
    filters: AuditLogFilters;
    /** Every active filter; the ones the toolbar already shows are skipped here. */
    chips: readonly AuditFilterChip[];
    onChange: (next: AuditLogFilters) => void;
}

interface PendingFocus {
    removed: AuditChipId;
    /** The chip after the removed one, which takes its place. */
    next: AuditChipId | null;
}

function isFocusLost(): boolean {
    return document.activeElement === null || document.activeElement === document.body;
}

/**
 * The active filters the toolbar does not show, each removable, and "Clear all". Clearing
 * returns to the page default, audit log views hidden, so it has nothing to do there.
 */
export default function ActiveFilterChips({ filters, chips, onChange }: ActiveFilterChipsProps) {
    const { t } = useTranslation();
    const text = useAuditText();
    const shown = chips.filter((chip) => chip.placement === 'chips');
    const removeButtons = useRef(new Map<AuditChipId, HTMLButtonElement>());
    const clearAllRef = useRef<HTMLButtonElement>(null);
    const pendingFocus = useRef<PendingFocus | null>(null);

    // A removed chip takes its focused button with it, which drops focus to the page. Focus
    // goes to the chip that took its place, else to Clear all, else to the last chip left.
    useEffect(() => {
        const pending = pendingFocus.current;
        if (pending === null || shown.some((chip) => chip.id === pending.removed)) return;
        pendingFocus.current = null;
        if (!isFocusLost()) return;
        const clearAll = clearAllRef.current;
        const last = shown.at(-1);
        const target =
            (pending.next === null ? undefined : removeButtons.current.get(pending.next)) ??
            (clearAll !== null && !clearAll.disabled ? clearAll : undefined) ??
            (last === undefined ? undefined : removeButtons.current.get(last.id));
        target?.focus();
    }, [shown]);

    function remove(chip: AuditFilterChip, index: number) {
        pendingFocus.current = { removed: chip.id, next: shown[index + 1]?.id ?? null };
        onChange(removeAuditFilterChip(filters, chip.id));
    }

    return (
        <div className={styles.row}>
            {shown.length > 0 && (
                <ul className={styles.chips} aria-label={t('auditLog.chips.region')}>
                    {shown.map((chip, index) => (
                        <FilterChip
                            key={chip.id}
                            label={text(chip.label)}
                            value={chip.value === null ? null : text(chip.value)}
                            onRemove={() => remove(chip, index)}
                            removeRef={(button) => {
                                if (button === null) removeButtons.current.delete(chip.id);
                                else removeButtons.current.set(chip.id, button);
                            }}
                        />
                    ))}
                </ul>
            )}
            <button
                ref={clearAllRef}
                type="button"
                className={styles.clearAll}
                disabled={isDefaultAuditLogFilters(filters)}
                onClick={() => onChange(DEFAULT_AUDIT_LOG_FILTERS)}>
                {t('auditLog.chips.clearAll')}
            </button>
        </div>
    );
}
